import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';

export interface BankChargesOption {
  label: string;
  value: string | number;
}

export interface BankChargesAttachment {
  name: string;
  presignedUrl: string;
}

/**
 * Shared "bank, reference, charges & documents" block — our bank account, the bank reference, the two
 * bank charges (banking fee + security fee) and the document attachments, in one section with a dynamic
 * title. Used by AP Payment (our bank *sends*) and Cash Funding (our bank *receives*); the only difference
 * is the title and whether the fees add to / subtract from the net, which the parent owns.
 *
 * The parent owns the reactive form and passes the FormControls; this component is pure presentation
 * (plus file pick / open events). Fee controls are optional, so a screen with no fees can still reuse it.
 */
@Component({
  selector: 'app-bank-charges-section',
  templateUrl: './bank-charges-section.component.html',
  styleUrl: './bank-charges-section.component.scss',
})
export class BankChargesSectionComponent {
  @Input() title = '';

  /**
   * Our bank account and the bank's reference for the transfer.
   *
   * <p>Optional in the same sense the fee controls are: a settlement where no money moves has neither. An
   * AP payment fully covered by a vendor advance (#4349) posts Dr AP / Cr Anticipo with no bank line and no
   * statement movement, so it passes null for both and the section renders what is left — its documents.</p>
   */
  @Input() bankAccountControl?: FormControl<string | null> | null;
  @Input() referenceControl?: FormControl<string | null> | null;
  @Input() bankingFeeControl?: FormControl<number | null>;
  @Input() securityFeeControl?: FormControl<number | null>;

  @Input() bankAccountOptions: BankChargesOption[] = [];

  /**
   * The provider bank account the money is sent TO (#5143). Optional — supply it and the destination
   * picker renders, omit it and the section is exactly what it was.
   *
   * <p>Cash Funding passes nothing: money arrives from a funder, and there is no provider destination to
   * choose. AP Payment and AP Advance both pass one, which is why this lives here rather than twice in
   * two screens that already share every other field in the block.</p>
   */
  @Input() destinationControl?: FormControl<string | null>;
  @Input() destinationOptions: BankChargesOption[] = [];
  @Input() destinationPlaceholder = 'Select a provider bank account';
  /** Shows the chosen destination but refuses changes — for a record that arrived with the account
   *  already decided upstream. Readonly rather than disabled on purpose: a disabled control drops out
   *  of `form.value`, which would post a null bank. */
  @Input() destinationReadonly = false;
  /** Shown under the destination when there ARE options — e.g. why the field is locked. Optional: with no
   *  hint the field simply carries none, exactly as before. */
  @Input() destinationHint = '';
  /** Shown when the provider has no account that can take this currency — which is the common case:
   *  most providers have no accounts on file at all. */
  @Input() noDestinationHint = 'This provider has no bank account in this currency.';
  @Input() currencyLabel = '';
  @Input() attachments: BankChargesAttachment[] = [];

  /** A persisted draft is required before attachments can be uploaded. */
  @Input() canAttach = false;
  /** Accepted upload types (also drives the file input accept attribute). */
  @Input() accept = '.pdf,.png,.jpg,.jpeg,.xlsx,.docx';

  @Input() bankAccountPlaceholder = 'Select a bank account';
  @Input() referencePlaceholder = 'e.g. TXN-BAC-0099831';
  @Input() referenceHint = 'Reference number from the bank confirmation or wire receipt.';
  @Input() noBankHint = 'No active bank account in this currency.';
  /** Fee hints differ by direction: deducted from the net (funding) vs added to the total (payment). */
  @Input() bankingFeeHint = 'Wire / transfer charge — deducted from the net.';
  @Input() securityFeeHint = 'Insurance / handling — deducted from the net.';

  @Output() fileSelected = new EventEmitter<File>();
  @Output() attachmentOpened = new EventEmitter<string>();

  isDragging = false;

  onFile(event: Event, input: HTMLInputElement): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.fileSelected.emit(file);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    if (!this.canAttach) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.fileSelected.emit(file);
  }

  fileName(name: string): string {
    return name.split('/').pop() || name;
  }
}
